import React from "react";
import { Box } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { logo_red } from "../../assets";
import { useContentSetting } from "../../Hooks/ContentSetting";
import { CAN_MANAGE_ROLES } from "../SchoolPortal/Tutors/TutorsShell";
import { iv, STEP_COLORS, BLOBS, blobKeyframes, ink, body, brand } from "./inviteTheme";

/**
 * The invitation page, at /instructors/join. This is the link the marketing
 * site points at to recruit instructors.
 *
 * IT IS A LANDING PAGE, WHICH MEANS THREE THINGS AND NOT FOUR
 * A title, a subtitle, and ONE component that carries the whole story. Earlier
 * versions had benefits, then a separate four-step "how it works", then an
 * FAQ, then a closing CTA; each section was defensible on its own and together
 * they were three screens of scrolling for a page whose only job is to get one
 * button pressed. Steps and benefits are the same four facts told twice, so
 * they are now one row of four: what you do, and what you get for doing it.
 *
 * WHY THIS EXISTS SEPARATELY FROM /instructors/signup
 * That page is a form; this is what you read before deciding to fill one in.
 * It also has to survive being clicked by someone already logged in, already
 * an instructor, or already at a school on XAir, which a signup form cannot.
 *
 * TWO WAYS TO BECOME AN INSTRUCTOR, AND THEY ARE NOT THE SAME SIGNUP
 * - Independent: create your own one-person school, at /instructors/signup.
 * - At a school already on XAir: your Owner or Manager adds you as staff, then
 *   you open your calendar from Teaching. There is no self-serve signup for
 *   that, because a second account would strand you outside your own school's
 *   students, so this page sends those people to log in rather than sign up.
 *
 * IT MUST NOT SIT INSIDE LogoOnlyLayout. That layout redirects anyone holding
 * a token to /dashboard, which would make this page unreachable for exactly
 * the visitors whose state it is here to recognise.
 */

// The one component's content. Each entry is a step AND the benefit of that
// step, deliberately fused: "set your rate" is the action, "you keep 85%" is
// the reason, and splitting them into two sections was what made this page
// long without making it say more.
const JOURNEY = [
  {
    icon: "💷",
    title: "Set your rate",
    body: "You price 10, 30 and 60 minute sessions. You keep 85%, XAir takes a flat 15%, and there is no joining fee.",
  },
  {
    icon: "🗓️",
    title: "Open your hours",
    body: "Set the hours you teach once and you are bookable across all of them. Block out anything you need.",
  },
  {
    icon: "🔔",
    title: "Students request you",
    body: "Your own students, plus other XAir schools if you opt in. Every request needs your approval.",
  },
  {
    icon: "🎧",
    title: "Teach, and get paid",
    body: "Every lesson is online, in a video room inside XAir, with nothing to install. Your share lands 72 hours after the lesson.",
  },
];

/**
 * Which of the five visitors this is. Read from the token and the cached user
 * rather than from an API call: this is the first page someone sees off a
 * marketing link, and a spinner in front of a pitch is a page that has already
 * lost them. Nothing here gates access, since every state's button lands on a
 * route that does its own real check, so a stale cache costs one extra click
 * and never a wrong door.
 */
function useVisitorState() {
  const { userInfo } = useContentSetting();
  if (!localStorage.getItem("token")) return { kind: "anonymous" };

  const role = userInfo?.role;
  const school = userInfo?.flightSchool;
  const teaches = role === "instructor" || CAN_MANAGE_ROLES.includes(role);

  if (!school) return { kind: "no_school" };
  if (teaches && school?.modules?.tutors) return { kind: "already_teaching", school };
  if (teaches) return { kind: "module_off", school, canManage: CAN_MANAGE_ROLES.includes(role) };
  return { kind: "wrong_role", school };
}

const Arrow = () => <span style={{ fontSize: "13px" }}>→</span>;

// Makes the next click the right one for whoever is reading, which is not the
// same click for all five of them.
function CallToAction({ visitor }) {
  const navigate = useNavigate();

  // The same keys AccountPopover and SchoolSidebar clear on log out, so this
  // leaves the session in the state the rest of the portal expects.
  const signOutTo = (path) => {
    localStorage.removeItem("token");
    localStorage.removeItem("user_data");
    localStorage.removeItem("impersonating_school");
    navigate(path, { replace: true });
  };

  const schoolName = visitor.school?.name || "your school";

  const states = {
    already_teaching: {
      note: "You are already an instructor on XAir. Your calendar, your rates and anyone waiting on your answer are all in the portal.",
      actions: (
        <Box component="button" type="button" sx={iv.btnPrimary} onClick={() => navigate("/school/teaching")}>
          Go to my teaching <Arrow />
        </Box>
      ),
    },
    module_off: {
      note: visitor.canManage
        ? `Teaching is not switched on for ${schoolName} yet. You can turn it on yourself in Settings.`
        : `Teaching is not switched on for ${schoolName} yet. Ask whoever owns your school's XAir account to enable it.`,
      actions: (
        <>
          {visitor.canManage && (
            <Box component="button" type="button" sx={iv.btnPrimary} onClick={() => navigate("/school/settings")}>
              Open settings <Arrow />
            </Box>
          )}
          <Box component="button" type="button" sx={iv.btnSecondary} onClick={() => navigate("/school/dashboard")}>
            Back to the portal
          </Box>
        </>
      ),
    },
    wrong_role: {
      note: `Instructors are added by whoever manages ${schoolName} on XAir. Ask them to add you as one, rather than making a second account that would sit outside your school's students.`,
      actions: (
        <Box component="button" type="button" sx={iv.btnSecondary} onClick={() => navigate("/school/dashboard")}>
          Back to the portal
        </Box>
      ),
    },
    // Signed in, but on an account with no school behind it. Both onward routes
    // live inside LogoOnlyLayout, which sends anyone holding a token to
    // /dashboard, so a plain link here would silently dead-end. Sign out first.
    no_school: {
      note: "You are signed in on an account that does not teach. Instructor accounts are separate, and setting one up is free.",
      actions: (
        <>
          <Box component="button" type="button" sx={iv.btnPrimary} onClick={() => signOutTo("/instructors/signup?from=invite")}>
            Sign out and get started <Arrow />
          </Box>
          <Box component="button" type="button" sx={iv.btnSecondary} onClick={() => signOutTo("/login")}>
            Sign out and log in
          </Box>
        </>
      ),
    },
    anonymous: {
      note: "Free to join, about a minute to set up. Already teach at a school that uses XAir? Log in instead, so you keep your own students.",
      actions: (
        <>
          <Box component={RouterLink} to="/instructors/signup?from=invite" sx={iv.btnPrimary}>
            Become an instructor <Arrow />
          </Box>
          <Box component={RouterLink} to="/login" sx={iv.btnSecondary}>
            I already have an account
          </Box>
        </>
      ),
    },
  };

  const state = states[visitor.kind] || states.anonymous;

  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px", justifyContent: "center" }}>
        {state.actions}
      </Box>
      <Box component="p" sx={{ ...iv.hint, maxWidth: "520px", margin: "14px auto 0", textAlign: "center" }}>
        {state.note}
      </Box>
    </>
  );
}

export default function BecomeInstructor() {
  const visitor = useVisitorState();

  return (
    <Box sx={iv.page}>
      <style>{blobKeyframes}</style>

      {BLOBS.map((b, i) => (
        <Box
          key={b.color}
          aria-hidden
          sx={{
            position: "absolute", width: b.size, height: b.size, borderRadius: "50%",
            top: b.top, left: b.left, right: b.right, bottom: b.bottom,
            background: `radial-gradient(circle, ${b.color}, transparent 70%)`,
            filter: "blur(14px)", pointerEvents: "none", zIndex: 0,
            animation: `ivFloat ${13 + i}s ease-in-out infinite`,
            animationDelay: b.delay,
          }}
        />
      ))}

      <Box sx={iv.container}>
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          py: { xs: 2.5, md: 3 },
        }}>
          <img src={logo_red} alt="XAir" style={{ height: "32px" }} />
          <Box component={RouterLink} to="/login" sx={{ ...iv.footLink, fontSize: "14px", textDecoration: "none" }}>
            Log in
          </Box>
        </Box>

        {/* Title, subtitle, buttons. Centred, because at this width a
            left-aligned headline with nothing beside it is a column again. */}
        <Box sx={{ textAlign: "center", pt: { xs: 2, md: 3 }, pb: { xs: 3.5, md: 4.5 }, animation: "ivRise 0.5s ease both" }}>
          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: "8px",
            background: "rgba(192,30,46,0.08)", color: brand, borderRadius: "999px",
            padding: "7px 16px", fontSize: "12.5px", fontWeight: 700,
            letterSpacing: "0.06em", textTransform: "uppercase", mb: 2,
          }}>
            For flight instructors
          </Box>

          <Box component="h1" sx={{
            ...iv.heading,
            fontSize: { xs: "34px", sm: "44px", md: "52px" },
            mb: 1.5,
          }}>
            Teach on XAir
          </Box>

          <Box component="p" sx={{
            ...iv.lead,
            fontSize: { xs: "16px", md: "17.5px" },
            maxWidth: "620px", margin: "0 auto 26px",
          }}>
            Student pilots here are looking for help on the subject they keep failing. Open your
            calendar and they can book you, at the price you set.
          </Box>

          <CallToAction visitor={visitor} />
        </Box>

        {/* The one component. Four steps, each carrying its own benefit. */}
        <Box sx={{
          display: "grid",
          // Four across from `md`, not `lg`. At `lg` a 1000px-wide laptop got a
          // 2x2 block, which is a second row and therefore a second screen.
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: { xs: 2, md: 2.5 },
          animation: "ivRise 0.6s 0.1s ease both",
        }}>
          {JOURNEY.map((step, i) => {
            const c = STEP_COLORS[i % STEP_COLORS.length];
            return (
              <Box
                key={step.title}
                sx={{
                  position: "relative",
                  background: `linear-gradient(180deg, ${c.tint} 0%, rgba(255,255,255,0.9) 46%), #fff`,
                  border: `1px solid ${c.soft}`,
                  borderRadius: "22px",
                  padding: { xs: "20px", md: "22px 20px" },
                  boxShadow: "0 14px 34px -22px rgba(26,26,26,0.4)",
                  transition: "transform 0.18s ease, box-shadow 0.18s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 22px 44px -22px rgba(26,26,26,0.45)",
                  },
                }}
              >
                <Box sx={{
                  width: 46, height: 46, borderRadius: "16px", background: c.soft,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "22px", mb: 1.5,
                }}>
                  {step.icon}
                </Box>
                <Box sx={{
                  fontSize: "11px", fontWeight: 800, letterSpacing: "0.1em",
                  color: c.strong, textTransform: "uppercase", mb: 0.75,
                }}>
                  Step {i + 1}
                </Box>
                <Box component="h2" sx={{
                  fontSize: "17px", fontWeight: 800, color: ink, letterSpacing: "-0.3px",
                  margin: "0 0 6px",
                }}>
                  {step.title}
                </Box>
                <Box component="p" sx={{
                  fontSize: "13.5px", color: body, lineHeight: 1.55, margin: 0,
                }}>
                  {step.body}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
